/* eslint-disable no-undef */
/**
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import React from 'react';
import { connect } from 'react-redux';
import xor from 'lodash/xor';
// @ts-ignore
import MarkerClusterer from '@google/markerclustererplus';
import { RootState } from '../../../../store';
import {
  defaultGroundStationsSvgProps,
  GroundStationsSvgProps,
} from './GroundStationsMarkerIcon';
import {
  HubsMarkerIcon
} from '../../../fleet/Hubs/HubsMarkerIcon';
import { requestHaloNetGroundStations } from '../../../../store/haloNetGroundStations/actions';
import { GroundStationData, GroundStationType } from '../../../../shared/entities/haloNet';
import { ThemeInterface } from '../../../common/theme/AppThemeInterface';
import appTheme from '../../../common/theme/appTheme.json';
import GroundStationNamePanel from './GroundStationNamePanel';

const theme: ThemeInterface = appTheme;
const MARKER_CUSTOM_FIELD_NAME_GROUND_STATION_DETAILS = 'groundStationDetails';

interface GroundStationsContainerProps {
  map: google.maps.Map;
}

interface GroundStationsContainerState {
  hoveredHubMarker?: any;
}

interface GroundStationMarker {
  marker: google.maps.Marker;
  count: number;
}

export interface GroundStationDetails extends GroundStationData {
  groundStationIconProps: GroundStationsSvgProps;
}

class GroundStationsContainer extends React.PureComponent<
  GroundStationsContainerProps & StateProps & DispatchProps,
  GroundStationsContainerState
> {
  private groundStationToMarkerMap: { [groundStationName: string]: GroundStationMarker };

  private hubMarkerClusterer: MarkerClusterer;

  private overlayView: google.maps.OverlayView;

  public constructor(props: GroundStationsContainerProps & StateProps & DispatchProps) {
    super(props);

    this.groundStationToMarkerMap = {};
    this.hubMarkerClusterer = null;

    this.overlayView = new google.maps.OverlayView();
    this.overlayView.setMap(props.map);

    this.state = {
      hoveredHubMarker: undefined,
    };
  }

  /**
   * Request the groundStations data from backend at component mount
   * and restore any ground stations that should be visible
   */
  public componentDidMount(): void {
    const { requestHaloNetGroundStations, groundStationsOnMap } = this.props;
    requestHaloNetGroundStations();

    if (groundStationsOnMap && groundStationsOnMap.length > 0) {
      groundStationsOnMap.forEach((groundStation: GroundStationData) => {
        this.addGroundStationToMap(groundStation);
      });

      if (this.hubMarkerClusterer) {
        this.hubMarkerClusterer.repaint();
      }
    }
  }

  public componentDidUpdate(prevProps: StateProps & DispatchProps): void {
    const { groundStationsOnMap } = this.props;
    // Get a list of groundStationId that have been either added or removed to/from the map
    const changedGroundStationsOnMap: GroundStationData[] = xor(groundStationsOnMap, prevProps.groundStationsOnMap);
    // Update only the changed markers
    if (changedGroundStationsOnMap && changedGroundStationsOnMap.length) {
      this.updateMarkersOnMap(changedGroundStationsOnMap);
    }
  }

  /**
   * Clean up markers and clusterer when component unmounts
   */
  public componentWillUnmount(): void {
    Object.values(this.groundStationToMarkerMap).forEach(({ marker }) => {
      marker.setMap(null);
    });

    this.groundStationToMarkerMap = {};

    if (this.hubMarkerClusterer) {
      this.hubMarkerClusterer.clearMarkers();
      this.hubMarkerClusterer = null;
    }

    if (this.overlayView) {
      this.overlayView.setMap(null);
    }
  }

  /**
   * Adds or removes the groundStation markers from the map based on the beams that have either been selected or deselected from showing on the map.
   * This function also updates the marker clusters.
   */
  private updateMarkersOnMap = (changedGroundStationsOnMap: GroundStationData[]): void => {
    const { groundStationsOnMap } = this.props;
    if (!groundStationsOnMap) {
      return;
    }

    changedGroundStationsOnMap.forEach((groundStation: GroundStationData) => {
      if (groundStationsOnMap.includes(groundStation)) {
        // Ground Station was added to the map
        this.addGroundStationToMap(groundStation);
      } else {
        // Ground Station was removed from the map
        this.removeGroundStationFromMap(groundStation);
      }
    });
    this.hubMarkerClusterer.repaint();
  };

  /**
   * Adds a single groundStation to the map
   */
  private addGroundStationToMap = (groundStation: GroundStationData): void => {
    if (groundStation) {
      // Get the groundStation marker if it already exists on the map
      const groundStationKey = groundStation.groundStationName;
      let groundStationMarker = this.groundStationToMarkerMap[groundStationKey];

      // Create a new groundStation marker only if not already on the map
      if (!groundStationMarker) {
        groundStationMarker = {
          marker: this.createGroundStationMarker(groundStation),
          count: 1,
        };
        this.groundStationToMarkerMap[groundStationKey] = groundStationMarker;
        // Add the new marker to the clusterer
        if (this.hubMarkerClusterer === null) {
          // Create clusterer for the first time
          this.createGroundStationMarkerClusterer([groundStationMarker.marker]);
        } else {
          // The clusterer already exists
          // Do not draw clusters to avoid flashing. They will be drawn using this.markerClusterer.repaint()
          this.hubMarkerClusterer.addMarker(groundStationMarker.marker, true);
        }
      } else {
        // Increase count for groundStation marker that already exists on the map
        groundStationMarker.count += 1;
      }
    }
  };

  /**
   * Removes a single groundStation from the map
   */
  private removeGroundStationFromMap = (groundStation: GroundStationData): void => {
    if (groundStation) {
      // Get marker from local map
      const groundStationMarker = this.groundStationToMarkerMap[groundStation.groundStationName];
      // Remove groundStation marker from the map or reduce its count
      if (groundStationMarker) {
        if (groundStationMarker.count === 1) {
          this.hubMarkerClusterer.removeMarker(groundStationMarker.marker, true);

          // Remove marker from map and local map
          groundStationMarker.marker.setMap(null);
          delete this.groundStationToMarkerMap[groundStation.groundStationName];
        } else {
          groundStationMarker.count -= 1;
        }
      }
    }
  };

  /**
   * Creates a Google Maps Marker object that includes the SVG to represent the groundStation marker
   */
  private createGroundStationMarker = (groundStation: GroundStationData): google.maps.Marker => {
    const { map } = this.props;
    // Get groundStation color from one of the groundStation beams (ASSUMPTION: a groundStation can only have beams of the same network)
    let groundStationColor = theme.groundStations.innerDiamondFill;

    const iconProps = {
      ...defaultGroundStationsSvgProps,
      innerDiamond: {
        ...defaultGroundStationsSvgProps.innerDiamond,
        fill: groundStationColor,
      },
    };
    const icon = HubsMarkerIcon(iconProps);
    const iconUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(icon)}`;

    const hubMarker = new google.maps.Marker({
      position: { lat: groundStation.location.latitude, lng: groundStation.location.longitude },
      map,
      icon: {
        url: iconUrl,
        anchor: new google.maps.Point(0, 0),
        scaledSize: new google.maps.Size(32, 32), // needed for the marker to be visible in IE 11
      },
    });

    // Add the groundStationDetails to a marker custom field for easy access when used to open marker name panel
    const groundStationDetails: GroundStationDetails = {
      ...groundStation,
      groundStationIconProps: iconProps,
    };
    hubMarker.set(MARKER_CUSTOM_FIELD_NAME_GROUND_STATION_DETAILS, groundStationDetails);

    // Add mouseover listener to set the current hovered marker in the component state
    hubMarker.addListener('mouseover', () => {
      this.setState({
        hoveredHubMarker: hubMarker,
      });
    });

    return hubMarker;
  };

  /**
   * Returns the Marker Icon Props for a cluster marker
   */
  private getClusterMarkerIconProps = (): GroundStationsSvgProps => {
    return { ...defaultGroundStationsSvgProps };
  };

  /**
   * Creates a MarkerClusterer object that will cluster hub markers on the map based on the zoom level.
   * The MarkerClusterer will use the icon defined in the SVG returned by HubsMarkerIcon call.
   */
  private createGroundStationMarkerClusterer = (addedGroundStationMarker: google.maps.Marker[]): void => {
    const { map } = this.props;

    const iconProps = this.getClusterMarkerIconProps();
    const icon = HubsMarkerIcon(iconProps);
    const iconUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(icon)}`;

    this.hubMarkerClusterer = new MarkerClusterer(map, addedGroundStationMarker, {
      styles: [
        {
          url: iconUrl,
          anchorIcon: [0, 0],
          width: theme.groundStations.iconWidth,
          height: theme.groundStations.iconHeight,
          textColor: 'transparent', // set textColor to theme.hubs.diamondSecondaryFill if cluster number needs to be shown
        },
      ],
      zoomOnClick: false,
    });

    // Add mouseover listener to set the current hovered marker in the component state
    this.hubMarkerClusterer.addListener('mouseover', (cluster: any) => {
      this.setState({
        hoveredHubMarker: cluster,
      });
    });
  };

  /**
   * Reset the hovered marker to close the previously hovered panel
   */
  private mouseOutOnPanelHandler = (): void => {
    this.setState({
      hoveredHubMarker: undefined,
    });
  };

  public render(): React.ReactComponentElement<'div', {}> | null {
    const { hoveredHubMarker } = this.state;

    // Render a single groundStation name panel
    if (hoveredHubMarker && 'get' in hoveredHubMarker) {
      const groundStationDetails: GroundStationDetails = hoveredHubMarker.get(
        MARKER_CUSTOM_FIELD_NAME_GROUND_STATION_DETAILS,
      );

      const point = this.overlayView
        .getProjection()
        .fromLatLngToContainerPixel(
          new google.maps.LatLng(groundStationDetails.location.latitude, groundStationDetails.location.longitude),
        );

      if (!point) {
        return null;
      }

      return (
        <GroundStationNamePanel
          groundStationName={groundStationDetails.groundStationName}
          iconProps={groundStationDetails.groundStationIconProps}
          left={Math.abs(point.x)}
          top={Math.abs(point.y)}
          mouseLeaveHandler={this.mouseOutOnPanelHandler}
        />
      );
    }

    // Hide panel if no marker is hovered
    return null;
  }
}

const mapStateToProps = (state: RootState, ownProps: GroundStationsContainerProps): any => {
  const visibleStations = state.haloNetGroundStations.visibleStations; // or selectGroundStationVisibleStations(state)
  const allGroundStations = (state.haloNetGroundStations.groundStations || []).filter(
    groundStation => groundStation.type === GroundStationType.GROUND_STATION_TYPE_RTE,
  );
  
  // Filter to only visible ground stations
  const groundStationsOnMap = allGroundStations?.filter(gs => visibleStations[gs.groundStationName]) || [];

  return {
    groundStations: allGroundStations,
    groundStationsOnMap,
  };
    // TODO: the following will be needed in future implementations to hide the next beam groundStation by default and show it only when the mouse hovers the next beam
    // nextBeam: state.ui.overlaysUiState[state.ui.overlaysCurrentContext]?.nextBeam,
    // hoveredBeamId: state.ui.hoveredBeamId,
};
type StateProps = ReturnType<typeof mapStateToProps>;

const mapDispatchToProps = {
  requestHaloNetGroundStations,
};
type DispatchProps = typeof mapDispatchToProps;

export default connect(mapStateToProps, mapDispatchToProps)(GroundStationsContainer);
